import {Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {NgTemplateOutlet} from '@angular/common';
import {AbstractControl, ControlValueAccessor, FormControl, FormsModule, ReactiveFormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator} from '@angular/forms';
import {
  Bounds, FieldErrors, FsdField, FsdGroup, FsdValues, applyAutoSelect, dayToUtc, utcDayOf, withFieldHints, XUi, getPath, isEmptySchema, optionsWithValue, FsdOption, JsonSchema, normalizeForSubmit, parseSchema,
  applicableKeys, prefill, prefillActivated, setPath, syncGenerated, validate, visibleGroups,
} from './fsd-schema';
import {countInvalidFields} from '../form-messages';
import {FsdBlock, SectionHeading} from './section-headings';
import {EncodingLadderComponent, normaliseLadder} from '../encoding-ladder/encoding-ladder.component';

/**
 * Generic `format_specific_data` form, driven entirely by the template's JSON Schema
 * (`template_capabilities.format_specific_data_schema`). It knows control types, never
 * templates or keys — see fsd-schema.ts for the contract.
 *
 * Used like the other format-specific components:
 * `<app-schema-format-specific formControlName="format_specific_data" [schema]="...">`.
 * The value it reports is already normalized for submit: fields of inactive branches are
 * absent, hidden fields carry their default, numbers are numbers.
 */
@Component({
  selector: 'app-schema-format-specific',
  templateUrl: './schema-format-specific.component.html',
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SchemaFormatSpecificComponent), multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => SchemaFormatSpecificComponent), multi: true},
  ],
  imports: [FormsModule, ReactiveFormsModule, NgTemplateOutlet, EncodingLadderComponent],
})
export class SchemaFormatSpecificComponent implements ControlValueAccessor, Validator, OnChanges {

  /** The JSON Schema, as the AWSJSON string the API returns or already parsed. */
  @Input() schema: string | JsonSchema | null = null;
  /** Prefix for element ids, so several items on one page stay unique. */
  @Input() idPrefix = 'fsd';
  /**
   * Duration of the item's source video in seconds; null while unknown or when the job has no video
   * track. Drives `x-ui.auto_select` on fields the operator has not touched.
   */
  @Input() sourceDurationSeconds: number | null = null;
  /** Fires on every change the operator makes (not on prefill, auto-select or writeValue). */
  /**
   * The parent's refused-submit flag: every field shows its errors, also those of values nobody
   * edited here (an imported or restored setting).
   */
  @Input() revealErrors = false;
  /** A ruler before the settings, when they follow other sections in the same card (VOD PO card). */
  @Input() ruleBefore = false;
  /**
   * x-ui hints by field path for fields the template's schema does not hint yet (e.g. VOD's Onboard
   * window as days); merged into the parsed schema, see withFieldHints.
   */
  @Input() fieldHints: { [path: string]: XUi } = {};
  /** Headings over groups (section-headings.ts); none: every group a standalone h3 (IFE). */
  @Input() sections: SectionHeading[] = [];
  @Output() edited = new EventEmitter<void>();

  parsed: JsonSchema | null = null;
  values: FsdValues = {};
  groups: FsdGroup[] = [];
  errors: FieldErrors = {};
  touchedKeys = new Set<string>();
  disabled = false;

  /** One control per `ladder` field, so the ladder editor's own validator counts. */
  private ladders = new Map<string, FormControl>();
  private bounds: Bounds = {};
  private written: FsdValues | null = null;
  private onChange: (v: any) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  get empty(): boolean {
    return isEmptySchema(this.parsed);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['schema'] || changes['fieldHints']) {
      this.parsed = withFieldHints(parseSchema(this.schema), this.fieldHints);
      this.applicable = null;
      // A new template re-creates the groups.
      this.schemaRev++;
      this.values = prefill(this.parsed, this.written ?? {});
      this.refresh(true);
    } else if (changes['sourceDurationSeconds']) {
      this.refresh(true);
    }
  }

  writeValue(value: any): void {
    this.written = value && typeof value === 'object' ? {...value} : null;
    // A value written in (a restored or loaded PO) is the operator's choice: auto-select leaves it. It replaces
    // the earlier choices too, so a key it no longer carries (after "Forget settings") is auto-selected again.
    this.touchedKeys = new Set(Object.keys(this.written ?? {}));
    this.values = prefill(this.parsed, this.written ?? {});
    this.applicable = null;
    this.refresh(false);
    this.ladders.forEach((c, path) => c.setValue(this.val(path) ?? null, {emitEvent: false}));
    // Report the prefilled, normalised value, so what the parent submits is never the raw value
    // it wrote (e.g. a restored PO with keys the schema no longer declares).
    if (this.parsed) {
      this.emit();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
    // Report the prefilled, normalized value once a parent listens.
    this.emit();
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(_: AbstractControl): ValidationErrors | null {
    const ladderInvalid = this.groups.some(g => g.fields.some(f => f.widget === 'ladder' && this.ladders.get(f.path)?.invalid));
    if (!Object.keys(this.errors).length && !ladderInvalid) return null;
    return {
      formatSpecificData: ladderInvalid ? {...this.errors, ladder: ['Invalid ladder.']} : this.errors,
      // One per field with errors (form-level '' included), the ladder as one field.
      invalidFields: Object.keys(this.errors).length + (ladderInvalid ? 1 : 0),
    };
  }

  // ── template API ──────────────────────────────────────────────────────────

  val(path: string): any {
    return getPath(this.values, path);
  }

  set(path: string, value: any): void {
    this.values = setPath(this.values, path, value);
    // A schema change prefills from this snapshot: it must hold the operator's edits, not only the
    // value last written in.
    this.written = setPath(this.written ?? {}, path, value);
    this.touchedKeys.add(path);
    this.onTouched();
    this.edited.emit();
    this.refresh(true);
  }

  toggleOption(field: FsdField, option: any, on: boolean): void {
    const current: any[] = Array.isArray(this.val(field.path)) ? this.val(field.path) : [];
    const next = on
      ? field.options.map(o => o.value).filter(v => v === option || current.includes(v))
      : current.filter(v => v !== option);
    this.set(field.path, next);
  }

  isSelected(field: FsdField, option: any): boolean {
    const current = this.val(field.path);
    return Array.isArray(current) && current.includes(option);
  }

  /**
   * The groups arranged for display: a `sections` heading collects its member groups (in its
   * order) where the first of them stands; any other group is a block of its own. `ruled` puts a
   * ruler before every block but the first.
   */
  get blocks(): FsdBlock[] {
    const blocks: FsdBlock[] = [];
    const placed = new Set<string>();
    for (const group of this.groups) {
      if (placed.has(group.name)) continue;
      const heading = this.sections.find(s => s.groups.includes(group.name));
      const members = heading
        ? heading.groups.map(n => this.groups.find(g => g.name === n)).filter((g): g is FsdGroup => !!g)
        : [group];
      members.forEach(g => placed.add(g.name));
      blocks.push({
        key: heading ? 'section-' + blocks.length : 'group-' + blocks.length,
        title: heading?.title ?? null,
        groups: members,
        columns: heading ? members.map(g => this.asColumn(g)) : [],
        ruled: false,
      });
    }
    blocks.forEach((b, i) => b.ruled = i > 0);
    return blocks;
  }

  /**
   * A section member as a column: its first field leads it, and a switch there becomes a Yes/No
   * select so the leads of all columns look alike (stable objects, so the form is not re-rendered).
   */
  private asColumn(group: FsdGroup): FsdGroup {
    const first = group.fields[0];
    if (first && !first.advanced) this.leadPaths.add(first.path);
    group.fields.forEach((f, i) => {
      const next = group.fields[i + 1];
      if (f.widget === 'multiselect' && !f.advanced && next?.widget === 'switch' && !next.advanced) {
        this.trailing.set(f.path, next);
        this.absorbed.add(next.path);
      }
    });
    if (!first || first.widget !== 'switch' || first.advanced) return group;
    let lead = this.leadSelects.get(first);
    if (!lead) {
      lead = {...first, widget: 'select', options: [{value: true, label: 'Yes'}, {value: false, label: 'No'}]};
      this.leadSelects.set(first, lead);
    }
    return {name: group.name, fields: [lead, ...group.fields.slice(1)]};
  }

  private leadSelects = new WeakMap<FsdField, FsdField>();

  /** Paths of the section columns' leading fields: they span their column. */
  readonly leadPaths = new Set<string>();

  readonly utcDayOf = utcDayOf;
  readonly dayToUtc = dayToUtc;

  /** In a section column: a multiselect's following switch, rendered as its last option-row sibling. */
  readonly trailing = new Map<string, FsdField>();
  /** Switches rendered inside a multiselect (see `trailing`), so not on their own. */
  readonly absorbed = new Set<string>();

  /**
   * Option texts longer than this do not fit a half-width select (measured: 26 characters were cut).
   * A sibling list (one group or nested object, its plain and its advanced part each) holding such a
   * select renders as one column --
   * every field in it full width (user decision on #2761: no zig-zag of half and full widths).
   */
  private static readonly LONG_OPTION = 20;
  private oneColumnFor: FsdGroup[] | null = null;
  private oneColumn = new Set<string>();

  private inOneColumn(f: FsdField): boolean {
    if (this.oneColumnFor !== this.groups) {
      this.oneColumnFor = this.groups;
      this.oneColumn = new Set();
      const isLong = (x: FsdField) => x.widget === 'select' && x.options.some(o => o.label.length > SchemaFormatSpecificComponent.LONG_OPTION);
      // Per part: a group's (or object's) plain fields and its "Advanced" fields are laid out apart,
      // so a long advanced select does not stretch the plain fields above it.
      const visit = (siblings: FsdField[]) => {
        for (const part of [siblings.filter(x => !x.advanced), siblings.filter(x => x.advanced)]) {
          if (part.some(isLong)) part.forEach(x => this.oneColumn.add(x.path));
        }
        siblings.filter(x => x.widget === 'object').forEach(x => visit(x.children));
      };
      this.groups.forEach(g => visit(g.fields));
    }
    return this.oneColumn.has(f.path);
  }

  fieldClass(f: FsdField): string {
    const longOptions = this.inOneColumn(f);
    const wide = longOptions || f.widget === 'textarea' || f.widget === 'multiselect' || f.widget === 'ladder' || this.leadPaths.has(f.path);
    return wide ? 'col-12' : 'col-12 col-md-6';
  }

  groupId(group: FsdGroup): string {
    return `${this.idPrefix}-group-${this.groups.indexOf(group)}`;
  }

  /** Bumped on every new schema; part of the groups' track key (see the template). */
  schemaRev = 0;

  hasPlain(group: FsdGroup): boolean {
    return group.fields.some(f => f.widget === 'object' ? this.childrenIn(f, false).length > 0 : !f.advanced);
  }

  hasAdvanced(group: FsdGroup): boolean {
    return group.fields.some(f => f.advanced || f.children.some(c => c.advanced));
  }

  /**
   * Whether the group's own "Advanced" part has fields: advanced fields, and objects with only
   * advanced fields. An object with plain fields keeps its advanced ones in its own fieldset.
   */
  hasGroupAdvanced(group: FsdGroup): boolean {
    return group.fields.some(f => f.widget === 'object'
      ? !this.childrenIn(f, false).length && this.childrenIn(f, true).length > 0
      : f.advanced);
  }

  /** A nested object's fields for the plain (false) or advanced (true) part; all of them if the object itself is advanced. */
  childrenIn(field: FsdField, advanced: boolean): FsdField[] {
    if (field.advanced) {
      return advanced ? field.children : [];
    }
    return field.children.filter(c => c.advanced === advanced);
  }

  /** Options of a select, plus the current value when it is not among them. */
  selectOptions(field: FsdField): FsdOption[] {
    return optionsWithValue(field, this.val(field.path));
  }

  tagsText(field: FsdField): string {
    const v = this.val(field.path);
    return Array.isArray(v) ? v.join(', ') : '';
  }

  setTags(field: FsdField, text: string): void {
    this.set(field.path, text.split(',').map(s => s.trim()).filter(s => s !== ''));
  }

  fieldId(field: FsdField): string {
    return `${this.idPrefix}-${field.path.replace('.', '-')}`;
  }

  /** Whether the field's own input/select/textarea sits right before its feedback line. */
  inputPrecedesFeedback(field: FsdField): boolean {
    return !['switch', 'multiselect', 'ladder'].includes(field.widget as string);
  }

  showErrors(field: FsdField): boolean {
    return !!this.errors[field.path] && (this.revealErrors || this.touchedKeys.has(field.path));
  }

  /** The control the ladder editor binds to; created once per field, fed from the values. */
  ladderControl(field: FsdField): FormControl {
    let c = this.ladders.get(field.path);
    if (!c) {
      c = new FormControl(this.val(field.path) ?? null);
      // Normalised, so a preset is sent with every value it inherits spelled out.
      c.valueChanges.subscribe(v => this.set(field.path, v ? normaliseLadder(v) : v));
      this.ladders.set(field.path, c);
      // The editor installs its validator while it initialises, after this form last validated; ask
      // the parent to validate again once it has, or a restored invalid ladder would count as valid.
      Promise.resolve().then(() => this.onValidatorChange());
    }
    return c;
  }

  // ── internals ─────────────────────────────────────────────────────────────

  /**
   * The values with every ladder normalised, also one nobody edited (a preset, a stored or restored
   * ladder): the editor only normalises on change, and a raw preset lacks values it inherits
   * (gop_in_seconds, gops_per_segment, the audio DRM tier).
   */
  private withNormalisedLadders(): FsdValues {
    let values = this.values;
    for (const field of this.groups.flatMap(g => g.fields.flatMap(f => [f, ...f.children]))) {
      const ladder = field.widget === 'ladder' ? getPath(values, field.path) : null;
      if (ladder && typeof ladder === 'object') {
        values = setPath(values, field.path, normaliseLadder(ladder));
      }
    }
    return values;
  }

  /** Keys applicable at the last refresh; null right after a new schema or written value. */
  private applicable: Set<string> | null = null;

  private refresh(emit: boolean): void {
    // Right after a new schema or value, prefill has run: compare from here, before the auto-select.
    const before = this.applicable ?? applicableKeys(this.parsed, this.values);
    this.values = applyAutoSelect(this.parsed, this.values, this.touchedKeys, this.sourceDurationSeconds);
    // A branch the change (or the auto-select) just activated gets its defaults, as on a fresh form.
    this.values = prefillActivated(this.parsed, before, this.values);
    this.applicable = applicableKeys(this.parsed, this.values);
    const synced = syncGenerated(this.parsed, this.values, this.bounds);
    this.values = synced.values;
    this.bounds = synced.bounds;
    this.groups = visibleGroups(this.parsed, this.values);
    this.errors = validate(this.parsed, this.values);
    if (emit) {
      this.emit();
    }
  }

  private emit(): void {
    // No schema yet (still loading, or failed): nothing is known to report. Emitting {} here would
    // overwrite the value the parent wrote in (e.g. saved settings) with an empty one.
    if (!this.parsed) return;
    this.onChange(normalizeForSubmit(this.parsed, this.withNormalisedLadders()));
    this.onValidatorChange();
  }
}
